import React, { Component, memo } from 'react';
import { Result, Button, Card } from 'antd';
import { 
  ExclamationCircleOutlined, 
  ReloadOutlined, 
  BugOutlined,
  HomeOutlined 
} from '@ant-design/icons';

class PerformanceErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Performance Dashboard Error:', error);
      console.error('Error Info:', errorInfo);
    }

    // Log error to external service in production
    if (process.env.NODE_ENV === 'production') {
      this.logErrorToService(error, errorInfo);
    }

    this.setState({
      error,
      errorInfo,
    });
  }

  logErrorToService = (error, errorInfo) => {
    // In a real application, you would send this to an error reporting service
    // like Sentry, LogRocket, or Bugsnag
    console.error('Error logged to service:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
    });
  };

  handleRetry = () => {
    const { retryCount } = this.state;
    const { maxRetries = 3 } = this.props;

    if (retryCount < maxRetries) {
      this.setState(prevState => ({
        hasError: false,
        error: null,
        errorInfo: null,
        retryCount: prevState.retryCount + 1,
      }));
    }
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  render() {
    const { hasError, error, retryCount } = this.state;
    const { children, fallback, maxRetries = 3 } = this.props;

    if (hasError) {
      // Custom fallback UI
      if (fallback) {
        return fallback(error, this.handleRetry);
      }

      // Default error UI
      return (
        <Card className="performance-error-boundary">
          <Result
            icon={<ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
            title="Performance Dashboard Error"
            subTitle={
              <div>
                <p>Something went wrong while loading the performance dashboard.</p>
                {process.env.NODE_ENV === 'development' && error && (
                  <details style={{ marginTop: 16, textAlign: 'left' }}>
                    <summary>Error Details</summary>
                    <pre style={{ 
                      background: '#f5f5f5', 
                      padding: 12, 
                      borderRadius: 4,
                      overflow: 'auto',
                      maxHeight: 200,
                      fontSize: 12
                    }}>
                      {error.toString()}
                    </pre>
                  </details>
                )}
              </div>
            }
            extra={[
              <Button 
                key="retry" 
                type="primary" 
                icon={<ReloadOutlined />}
                onClick={this.handleRetry}
                disabled={retryCount >= maxRetries}
                style={{ marginRight: 8 }}
              >
                {retryCount >= maxRetries ? 'Max Retries Reached' : 'Retry'}
              </Button>,
              <Button 
                key="reload" 
                icon={<ReloadOutlined />}
                onClick={this.handleReload}
                style={{ marginRight: 8 }}
              >
                Reload Page
              </Button>,
              <Button 
                key="home" 
                icon={<HomeOutlined />}
                onClick={this.handleGoHome}
              >
                Go to Dashboard
              </Button>,
            ]}
          />
          
          {retryCount > 0 && (
            <div style={{ 
              textAlign: 'center', 
              marginTop: 16, 
              color: '#8c8c8c',
              fontSize: 12 
            }}>
              Retry attempt: {retryCount}/{maxRetries}
            </div>
          )}
        </Card>
      );
    }

    return children;
  }
}

// HOC for wrapping components with error boundary
export const withPerformanceErrorBoundary = (WrappedComponent, errorBoundaryProps = {}) => {
  const WithErrorBoundary = memo((props) => (
    <PerformanceErrorBoundary {...errorBoundaryProps}>
      <WrappedComponent {...props} />
    </PerformanceErrorBoundary>
  ));

  WithErrorBoundary.displayName = `withPerformanceErrorBoundary(${WrappedComponent.displayName || WrappedComponent.name})`;
  
  return WithErrorBoundary;
};

// Hook for error boundary functionality
export const usePerformanceErrorHandler = () => {
  const handleError = (error, errorInfo) => {
    console.error('Performance Error:', error);
    
    if (process.env.NODE_ENV === 'production') {
      // Log to external service
      console.error('Error logged to service:', {
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo?.componentStack,
        timestamp: new Date().toISOString(),
      });
    }
  };

  const handleAsyncError = (error) => {
    console.error('Performance Async Error:', error);
    
    if (process.env.NODE_ENV === 'production') {
      // Log to external service
      console.error('Async Error logged to service:', {
        error: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString(),
      });
    }
  };

  return {
    handleError,
    handleAsyncError,
  };
};

export default PerformanceErrorBoundary;
